// =============================================================================
// OmniVoice — browser realtime voice client
//
// Speaks the exact protocol of `services/omnivoice-gateway`:
//   mic → WebSocket(/api/omnivoice/realtime?token&tenant_id) → gateway → OpenAI
//   OpenAI → gateway → WebSocket → speaker
//
// The gateway configures the session (24 kHz PCM in/out, server VAD,
// transcription) server-side, so this client only: streams
// `input_audio_buffer.append` frames of 24 kHz base64 PCM16, and plays back
// `response.output_audio.delta` frames (GA name; the legacy beta
// `response.audio.delta` is still parsed). Server VAD drives turn-taking;
// barge-in cancels queued playback.
//
// Everything DOM/network lives here; the wire framing/parse lives in
// ./protocol (pure, unit-tested). This class throws from connect() on any setup
// failure so the surface can fall back to the turn-based session.
// =============================================================================

import {
  REALTIME_SAMPLE_RATE,
  GATEWAY_CLOSE_CODE,
  buildAudioAppendFrame,
  buildRealtimeUrl,
  buildVisionContextFrame,
  decodePcm16Base64,
  encodePcm16Base64,
  parseServerEvent,
  resampleLinear,
} from './protocol';

export type RealtimeConnectionState =
  | 'connecting'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'closed';

export interface RealtimeClientOptions {
  /** Gateway base URL (http/https or ws/wss). */
  gatewayUrl: string;
  /** Supabase access token (query-param auth on the gateway). */
  token: string;
  /** Organization id (required by the gateway). */
  tenantId: string;
  callbacks?: RealtimeClientCallbacks;
  /**
   * Max ms to wait for the session to become READY (socket open AND the first
   * server event confirming the upstream session, e.g. `session.created`)
   * before failing. Default 8000.
   */
  connectTimeoutMs?: number;
}

export interface RealtimeClientCallbacks {
  onStateChange?: (state: RealtimeConnectionState) => void;
  onAudioLevel?: (level: number) => void;
  onUserTranscript?: (text: string) => void;
  onAssistantTranscript?: (text: string, final: boolean) => void;
  onError?: (err: Error) => void;
  onClose?: (code: number, reason: string) => void;
}

const FRAME_SIZE = 4096; // ScriptProcessor buffer (samples @ capture rate)

// --- Anti-self-trigger + barge-in gate (mirrors elevenLabsClient) ------------
// The 24 kHz assistant audio plays through a SEPARATE playback AudioContext with
// no reliable AEC loopback reference, so it leaks into the mic and self-triggers
// the gateway's server VAD (`speech_started`) → the assistant cuts itself off /
// talks over the user. While assistant audio is actively playing we gate
// outgoing mic frames unless the mic energy clearly crosses a barge-in threshold
// (a real interruption), in which case we flush queued playback locally and let
// the frame through so the server VAD also cancels the in-flight response.
// The RMS gate is a conservative runtime hypothesis — see elevenLabsClient.ts.
const BARGE_IN_RMS = 0.08;
const BARGE_IN_FRAMES = 2;
const GATE_HANGOVER_MS = 250;

export class OmniVoiceRealtimeClient {
  private readonly opts: RealtimeClientOptions;
  private ws: WebSocket | null = null;

  // Capture graph.
  private micStream: MediaStream | null = null;
  private captureCtx: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;

  // Playback graph (fixed 24 kHz to match the wire).
  private playbackCtx: AudioContext | null = null;
  private nextPlaybackTime = 0;
  private readonly scheduled = new Set<AudioBufferSourceNode>();

  // Gate state (see BARGE_IN_* / GATE_HANGOVER_MS above).
  private lastPlaybackEndedAt = 0; // Date.now() when the queue last drained; 0 = not gating.
  private speechFramesDuringPlayback = 0;

  private state: RealtimeConnectionState = 'connecting';
  private closedOnce = false;

  constructor(opts: RealtimeClientOptions) {
    this.opts = opts;
  }

  getState(): RealtimeConnectionState {
    return this.state;
  }

  private setState(state: RealtimeConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    this.opts.callbacks?.onStateChange?.(state);
  }

  /**
   * Open the socket, start mic capture and wait for the session to be READY.
   *
   * READY contract: `connect()` resolves only after the first server event that
   * proves the upstream Realtime session exists (`session.created` /
   * `session.updated`, relayed by the gateway) — NOT on `ws.onopen`. The
   * gateway opens the browser socket before it authenticates the caller and
   * before it dials OpenAI, so `onopen` proves nothing; a relay that dies 1 s
   * later (bad key, retired API, auth failure) must reject here so the caller
   * can fall back to turn-based voice instead of losing the overlay.
   *
   * Rejections: mic denied, socket refused, close/error before READY, or no
   * READY event within `connectTimeoutMs` (default 8 s).
   */
  async connect(): Promise<void> {
    this.setState('connecting');
    const url = buildRealtimeUrl(this.opts.gatewayUrl, {
      token: this.opts.token,
      tenantId: this.opts.tenantId,
    });

    // 1) Mic first — a denied mic should fail fast without burning a socket.
    await this.startCapture();

    // 2) Socket. Resolve on the first session-confirming server event; reject
    //    on error/close/timeout before that.
    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const timeoutMs = this.opts.connectTimeoutMs ?? 8000;

      const fail = (err: Error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.teardownCapture();
        try {
          this.ws?.close(1000, 'connect failed');
        } catch {
          /* noop */
        }
        this.ws = null;
        reject(err);
      };

      const timer = setTimeout(() => {
        fail(new Error('OmniVoice connect timed out (no session confirmation)'));
      }, timeoutMs);

      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch (err) {
        clearTimeout(timer);
        this.teardownCapture();
        reject(err instanceof Error ? err : new Error('WebSocket construction failed'));
        return;
      }
      ws.binaryType = 'arraybuffer';
      this.ws = ws;

      // onopen does NOT settle the promise — the gateway still has to
      // authenticate us and open the upstream OpenAI session. Stay 'connecting'.
      ws.onopen = null;

      ws.onerror = () => {
        if (settled) {
          this.opts.callbacks?.onError?.(new Error('OmniVoice socket error'));
          return;
        }
        fail(new Error('OmniVoice socket error'));
      };

      ws.onclose = (ev) => {
        const reason = ev.reason || GATEWAY_CLOSE_CODE[ev.code] || `closed (${ev.code})`;
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          this.teardownCapture();
          this.ws = null;
          reject(new Error(`OmniVoice connection refused: ${reason}`));
          return;
        }
        this.handleClose(ev.code, reason);
      };

      ws.onmessage = (ev) => {
        if (!settled) {
          // Pre-READY: watch for the session confirmation (or a fatal error).
          const evt = this.peekEvent(ev.data);
          if (evt?.kind === 'session_ready') {
            settled = true;
            clearTimeout(timer);
            this.setState('listening');
            this.wireCaptureToSocket();
            resolve();
            return;
          }
          if (evt?.kind === 'error') {
            fail(new Error(`OmniVoice session error: ${evt.message}`));
            return;
          }
          return; // ignore anything else until the session is confirmed
        }
        this.handleMessage(ev.data);
      };
    });
  }

  /**
   * Inject a live camera observation into the realtime session as text context
   * (non-blocking, no-op if the socket is not open). Does NOT trigger a spoken
   * response — it just gives NEXUS awareness of what the camera currently sees.
   */
  sendVisionContext(description: string): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const text = description.trim();
    if (!text) return;
    try {
      ws.send(buildVisionContextFrame(text));
    } catch {
      /* socket closing — ignore */
    }
  }

  /** Tear everything down. Safe to call multiple times. */
  stop(): void {
    try {
      this.ws?.close(1000, 'client stop');
    } catch {
      /* noop */
    }
    this.ws = null;
    this.teardownCapture();
    this.teardownPlayback();
    this.setState('closed');
  }

  // ---- capture ------------------------------------------------------------

  private async startCapture(): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    this.micStream = stream;

    const Ctx: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx();
    this.captureCtx = ctx;
    this.source = ctx.createMediaStreamSource(stream);
    // ScriptProcessorNode is deprecated but universally supported and needs no
    // separate worklet module (which Vite would have to serve). Adequate here.
    this.processor = ctx.createScriptProcessor(FRAME_SIZE, 1, 1);
    // Connect through to destination so the processor keeps firing (output is
    // silent — we never write to it).
    this.source.connect(this.processor);
    this.processor.connect(ctx.destination);
  }

  private wireCaptureToSocket(): void {
    if (!this.processor || !this.captureCtx) return;
    const inputRate = this.captureCtx.sampleRate;

    this.processor.onaudioprocess = (e: AudioProcessingEvent) => {
      const ws = this.ws;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);

      // RMS for the orb level meter AND the self-trigger gate below.
      let sum = 0;
      for (let i = 0; i < input.length; i++) sum += input[i] * input[i];
      const rms = Math.sqrt(sum / input.length);
      // Mic level for the orb while listening.
      if (this.state === 'listening') {
        this.opts.callbacks?.onAudioLevel?.(Math.min(1, rms * 4));
      }

      // Anti-self-trigger + barge-in: while our own audio is playing, only let
      // the mic through if it clearly crosses the barge-in threshold; otherwise
      // it is our TTS echo and feeding it to the server VAD makes the assistant
      // talk over the user / cut itself off.
      if (this.isAssistantAudioActive()) {
        if (rms > BARGE_IN_RMS && ++this.speechFramesDuringPlayback >= BARGE_IN_FRAMES) {
          // Genuine barge-in: flush queued assistant audio locally so buffered
          // deltas stop, then stream this frame — the gateway's server VAD will
          // also fire `speech_started` and cancel the in-flight response.
          this.cancelPlayback();
          this.setState('listening');
          this.speechFramesDuringPlayback = 0;
        } else {
          if (rms <= BARGE_IN_RMS) this.speechFramesDuringPlayback = 0;
          return; // gate: suppress echo / still confirming a barge-in
        }
      }

      const resampled = resampleLinear(input, inputRate, REALTIME_SAMPLE_RATE);
      const base64 = encodePcm16Base64(resampled);
      try {
        ws.send(buildAudioAppendFrame(base64));
      } catch {
        /* socket closing — ignore */
      }
    };
  }

  private teardownCapture(): void {
    if (this.processor) {
      this.processor.onaudioprocess = null;
      try {
        this.processor.disconnect();
      } catch {
        /* noop */
      }
      this.processor = null;
    }
    try {
      this.source?.disconnect();
    } catch {
      /* noop */
    }
    this.source = null;
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
    void this.captureCtx?.close().catch(() => undefined);
    this.captureCtx = null;
  }

  // ---- playback -----------------------------------------------------------

  private ensurePlaybackCtx(): AudioContext {
    if (this.playbackCtx) return this.playbackCtx;
    const Ctx: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    // Fixed sample rate to match the 24 kHz PCM16 wire; no resampling needed.
    this.playbackCtx = new Ctx({ sampleRate: REALTIME_SAMPLE_RATE });
    this.nextPlaybackTime = this.playbackCtx.currentTime;
    return this.playbackCtx;
  }

  private enqueueAudio(base64: string): void {
    const float = decodePcm16Base64(base64);
    if (float.length === 0) return;
    const ctx = this.ensurePlaybackCtx();

    const buffer = ctx.createBuffer(1, float.length, REALTIME_SAMPLE_RATE);
    // .set (vs copyToChannel) sidesteps the ArrayBuffer/SharedArrayBuffer
    // generic mismatch on Float32Array in newer TS lib typings.
    buffer.getChannelData(0).set(float);

    const node = ctx.createBufferSource();
    node.buffer = buffer;
    node.connect(ctx.destination);

    const now = ctx.currentTime;
    const startAt = Math.max(now, this.nextPlaybackTime);
    node.start(startAt);
    this.nextPlaybackTime = startAt + buffer.duration;

    this.scheduled.add(node);
    node.onended = () => {
      this.scheduled.delete(node);
      // Back to listening once the queue has fully drained.
      if (this.scheduled.size === 0 && this.state === 'speaking') {
        // Start the gate hangover window (tail echo can still self-trigger).
        this.lastPlaybackEndedAt = Date.now();
        this.setState('listening');
      }
    };
    this.setState('speaking');
  }

  /** True while our own audio is playing OR within the post-playback hangover. */
  private isAssistantAudioActive(): boolean {
    if (this.scheduled.size > 0) return true;
    if (this.lastPlaybackEndedAt === 0) return false;
    return Date.now() - this.lastPlaybackEndedAt < GATE_HANGOVER_MS;
  }

  /** Barge-in: stop and drop all queued audio (user started speaking). */
  private cancelPlayback(): void {
    for (const node of this.scheduled) {
      try {
        node.onended = null;
        node.stop();
      } catch {
        /* already stopped */
      }
    }
    this.scheduled.clear();
    if (this.playbackCtx) this.nextPlaybackTime = this.playbackCtx.currentTime;
    // Clear the hangover: after an explicit flush (barge-in / server
    // speech_started) the mic must stream immediately so the user is heard.
    this.lastPlaybackEndedAt = 0;
    this.speechFramesDuringPlayback = 0;
  }

  private teardownPlayback(): void {
    this.cancelPlayback();
    void this.playbackCtx?.close().catch(() => undefined);
    this.playbackCtx = null;
  }

  // ---- inbound ------------------------------------------------------------

  /** Decode + parse one raw frame without acting on it (pre-READY sniffing). */
  private peekEvent(data: unknown): ReturnType<typeof parseServerEvent> | null {
    let raw: string;
    if (typeof data === 'string') raw = data;
    else if (data instanceof ArrayBuffer) raw = new TextDecoder().decode(data);
    else return null;
    return parseServerEvent(raw);
  }

  private handleMessage(data: unknown): void {
    const evt = this.peekEvent(data);
    if (!evt) return;

    switch (evt.kind) {
      case 'audio':
        this.enqueueAudio(evt.base64);
        break;
      case 'user_transcript':
        if (evt.text) this.opts.callbacks?.onUserTranscript?.(evt.text);
        break;
      case 'assistant_transcript_delta':
        if (evt.text) this.opts.callbacks?.onAssistantTranscript?.(evt.text, false);
        break;
      case 'assistant_transcript_done':
        if (evt.text) this.opts.callbacks?.onAssistantTranscript?.(evt.text, true);
        break;
      case 'speech_started':
        // User (re)started talking — cancel any in-flight assistant audio.
        this.cancelPlayback();
        this.setState('listening');
        break;
      case 'response_done':
        if (this.scheduled.size === 0 && this.state !== 'listening') {
          this.setState('listening');
        }
        break;
      case 'error':
        this.opts.callbacks?.onError?.(new Error(evt.message));
        break;
      default:
        break;
    }
  }

  private handleClose(code: number, reason: string): void {
    if (this.closedOnce) return;
    this.closedOnce = true;
    this.teardownCapture();
    this.teardownPlayback();
    this.setState('closed');
    this.opts.callbacks?.onClose?.(code, reason);
  }
}
